/**
 * 同意書セクション 1 件分のエディター。
 *
 * v3.2.26: リストセクションは「大項目（タイトル+説明+子項目）」構造に。
 * consent_check セクションを新規対応。
 *
 * signature セクションは「ラベル名のみ編集可、削除不可」の特殊扱い。
 */
import type {
  ConsentListItem,
  ConsentSection,
  ConsentSectionType,
} from '@goenhub/shared';
import { ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { ImageFieldEditor } from '@/pages/landings/components/ImageFieldEditor';

import { CONSENT_SECTION_DEFS } from '../sectionDefs';

interface Props {
  section: ConsentSection;
  index: number;
  total: number;
  onChange: (next: ConsentSection) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function asListItems(v: unknown): ConsentListItem[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((it): ConsentListItem | null => {
      if (typeof it === 'string') {
        // 旧形式 fallback (移行前のローカル state ガード)
        return { title: it };
      }
      if (it && typeof it === 'object' && typeof (it as { title?: unknown }).title === 'string') {
        const obj = it as Partial<ConsentListItem>;
        return {
          title: obj.title ?? '',
          description: typeof obj.description === 'string' ? obj.description : undefined,
          children: Array.isArray(obj.children)
            ? obj.children.filter((c): c is string => typeof c === 'string')
            : undefined,
        };
      }
      return null;
    })
    .filter((x): x is ConsentListItem => x !== null);
}

export function ConsentSectionEditor({
  section,
  index,
  total,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
}: Props) {
  const def = CONSENT_SECTION_DEFS[section.type];
  const isSignature = section.type === 'signature';
  const Icon = def.icon;

  const setConfig = (next: Record<string, unknown>) => {
    onChange({ ...section, config: { ...section.config, ...next } });
  };

  return (
    <div className="rounded-lg bg-[var(--bg-surface)]">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] min-w-0">
          <GripVertical className="size-3.5 flex-shrink-0" />
          <Icon className="size-3.5 flex-shrink-0" />
          <span className="font-medium text-[var(--text-primary)] truncate">
            {def.label}
          </span>
          <span className="text-[10px]">#{index + 1}</span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMoveUp}
            disabled={isSignature || index === 0}
            title="上へ"
            className="size-7 p-0"
          >
            <ArrowUp className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onMoveDown}
            disabled={isSignature || index >= total - 2}
            title="下へ"
            className="size-7 p-0"
          >
            <ArrowDown className="size-3.5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={isSignature}
            title={isSignature ? '署名フィールドは削除不可' : '削除'}
            className="size-7 p-0 text-[var(--text-muted)] hover:text-red-500"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      <div className="p-3 space-y-2">
        <SectionTypeEditor
          type={section.type}
          config={section.config}
          setConfig={setConfig}
        />
      </div>
    </div>
  );
}

function SectionTypeEditor({
  type,
  config,
  setConfig,
}: {
  type: ConsentSectionType;
  config: Record<string, unknown>;
  setConfig: (next: Record<string, unknown>) => void;
}) {
  switch (type) {
    case 'heading':
      return (
        <div>
          <Label className="text-xs">見出しテキスト</Label>
          <Input
            value={asString(config.text)}
            onChange={(e) => setConfig({ text: e.target.value })}
            placeholder="例: 第1条 利用目的"
          />
        </div>
      );

    case 'paragraph':
      return (
        <div>
          <Label className="text-xs">本文</Label>
          <Textarea
            value={asString(config.text)}
            onChange={(e) => setConfig({ text: e.target.value })}
            rows={4}
            placeholder="同意書の本文を入力…"
          />
        </div>
      );

    case 'bullet_list':
    case 'numbered_list':
      return (
        <ListItemsEditor
          items={asListItems(config.items)}
          onChange={(items) => setConfig({ items })}
        />
      );

    case 'callout':
      return (
        <div className="space-y-2">
          <div>
            <Label className="text-xs">注意書きテキスト</Label>
            <Textarea
              value={asString(config.text)}
              onChange={(e) => setConfig({ text: e.target.value })}
              rows={3}
            />
          </div>
          <div>
            <Label className="text-xs">トーン</Label>
            <select
              value={asString(config.tone) || 'info'}
              onChange={(e) => setConfig({ tone: e.target.value })}
              className="w-full px-2 py-1.5 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] text-sm"
            >
              <option value="info">情報</option>
              <option value="warning">注意</option>
              <option value="danger">重要</option>
            </select>
          </div>
        </div>
      );

    case 'divider':
      return (
        <p className="text-xs text-[var(--text-muted)] italic">
          区切り線（設定項目なし）
        </p>
      );

    case 'image':
      return (
        <div className="space-y-2">
          <ImageFieldEditor
            label="画像"
            value={asString(config.storagePath) || null}
            onChange={(v) => setConfig({ storagePath: v })}
            storagePath="consent-documents/imgs"
          />
          <div>
            <Label className="text-xs">代替テキスト (任意)</Label>
            <Input
              value={asString(config.alt)}
              onChange={(e) => setConfig({ alt: e.target.value })}
              placeholder="画像の説明"
            />
          </div>
        </div>
      );

    case 'signature':
      return (
        <div className="space-y-2">
          <p className="text-[11px] text-[var(--text-muted)]">
            公開画面でデジタル署名インプット（氏名・署名日）として表示されます。
          </p>
          <div>
            <Label className="text-xs">セクション見出し</Label>
            <Input
              value={asString(config.label)}
              onChange={(e) => setConfig({ label: e.target.value })}
              placeholder="署名"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">氏名フィールド ラベル</Label>
              <Input
                value={asString(config.signatureFieldLabel)}
                onChange={(e) =>
                  setConfig({ signatureFieldLabel: e.target.value })
                }
                placeholder="氏名"
              />
            </div>
            <div>
              <Label className="text-xs">日付フィールド ラベル</Label>
              <Input
                value={asString(config.dateFieldLabel)}
                onChange={(e) => setConfig({ dateFieldLabel: e.target.value })}
                placeholder="署名日"
              />
            </div>
          </div>
        </div>
      );

    case 'consent_check':
      return (
        <div className="space-y-2">
          <p className="text-[11px] text-[var(--text-muted)]">
            提出条件として **チェック ON 必須** になります。複数設置すると全てチェックが必要。
          </p>
          <div>
            <Label className="text-xs">同意文</Label>
            <Textarea
              value={asString(config.statement)}
              onChange={(e) => setConfig({ statement: e.target.value })}
              rows={2}
              placeholder="例: 上記の内容に同意して申し込みをします。"
            />
          </div>
        </div>
      );

    default:
      return null;
  }
}

// ─────────────────────────────────────────────
// リスト項目エディター (大項目 + 説明 + 子項目)
// ─────────────────────────────────────────────

function ListItemsEditor({
  items,
  onChange,
}: {
  items: ConsentListItem[];
  onChange: (next: ConsentListItem[]) => void;
}) {
  const update = (idx: number, patch: Partial<ConsentListItem>) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };
  const move = (idx: number, delta: -1 | 1) => {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = [...items];
    const [m] = next.splice(idx, 1);
    next.splice(newIdx, 0, m);
    onChange(next);
  };
  const add = () => {
    onChange([...items, { title: '', description: '', children: [] }]);
  };
  const addChild = (idx: number) => {
    const next = [...items];
    const c = next[idx].children ?? [];
    next[idx] = { ...next[idx], children: [...c, ''] };
    onChange(next);
  };
  const updateChild = (idx: number, cIdx: number, value: string) => {
    const next = [...items];
    const c = [...(next[idx].children ?? [])];
    c[cIdx] = value;
    next[idx] = { ...next[idx], children: c };
    onChange(next);
  };
  const removeChild = (idx: number, cIdx: number) => {
    const next = [...items];
    const c = (next[idx].children ?? []).filter((_, i) => i !== cIdx);
    next[idx] = { ...next[idx], children: c };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs">項目（大項目ごとに分けて入力）</Label>
      {items.length === 0 && (
        <p className="text-[11px] text-[var(--text-muted)] italic">
          項目がありません。下のボタンから追加してください。
        </p>
      )}
      {items.map((it, i) => (
        <div
          key={i}
          className="rounded bg-[var(--bg-elevated)] p-2 space-y-1.5"
        >
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-[var(--text-muted)] font-mono w-6">
              #{i + 1}
            </span>
            <Input
              value={it.title}
              onChange={(e) => update(i, { title: e.target.value })}
              placeholder="大項目タイトル"
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => move(i, -1)}
              disabled={i === 0}
              className="size-7 p-0"
              title="上へ"
            >
              <ArrowUp className="size-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => move(i, 1)}
              disabled={i >= items.length - 1}
              className="size-7 p-0"
              title="下へ"
            >
              <ArrowDown className="size-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(i)}
              className="size-7 p-0 text-[var(--text-muted)] hover:text-red-500"
              title="削除"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
          <Textarea
            value={it.description ?? ''}
            onChange={(e) => update(i, { description: e.target.value })}
            rows={2}
            placeholder="補足説明（任意、空なら非表示）"
            className="text-sm"
          />

          {/* 子項目 */}
          {(it.children ?? []).length > 0 && (
            <div className="pl-4 space-y-1 mt-1">
              {(it.children ?? []).map((c, cIdx) => (
                <div key={cIdx} className="flex items-center gap-1">
                  <span className="text-[10px] text-[var(--text-muted)]">↳</span>
                  <Input
                    value={c}
                    onChange={(e) => updateChild(i, cIdx, e.target.value)}
                    placeholder="子項目"
                    className="flex-1 text-sm"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeChild(i, cIdx)}
                    className="size-6 p-0 text-[var(--text-muted)] hover:text-red-500"
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => addChild(i)}
            className="text-[11px] text-[var(--accent)] hover:underline ml-4"
          >
            + 子項目を追加
          </button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={add}
        className="w-full"
      >
        <Plus className="size-3.5 mr-1" />
        大項目を追加
      </Button>
    </div>
  );
}
